import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { cadCurrencyFormat } from "./constants";

function toNumberOrNull(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatRevisionStatus(status) {
  if (!status) return "Not flagged";
  if (status === "deviation_threshold") return "Deviation threshold exceeded";
  return String(status)
    .split("_")
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");
}

function formatWindow(windowName) {
  if (!windowName) return "Unknown";
  return String(windowName).charAt(0).toUpperCase() + String(windowName).slice(1);
}

function PriceDeviationBars({ baselineUnitPrice, currentUnitPrice, thresholdUnitPrice }) {
  const maxValue = Math.max(baselineUnitPrice || 0, currentUnitPrice || 0, thresholdUnitPrice || 0, 0.01);
  const rows = [
    { label: "Baseline median", value: baselineUnitPrice, color: "#6c757d" },
    { label: "Current row", value: currentUnitPrice, color: "#1976d2" },
    { label: "Threshold line", value: thresholdUnitPrice, color: "#f57c00" },
  ];

  return (
    <Stack spacing={1}>
      {rows.map((row) => (
        <Stack key={row.label} direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ width: 120, flexShrink: 0 }}>
            {row.label}
          </Typography>
          <Box sx={{ flexGrow: 1, backgroundColor: "#e0e0e0", height: 8, borderRadius: 999 }}>
            <Box
              sx={{
                width: `${Math.max(2, Math.min(100, (row.value / maxValue) * 100))}%`,
                backgroundColor: row.color,
                height: 8,
                borderRadius: 999,
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ width: 84, textAlign: "right", flexShrink: 0 }}>
            {cadCurrencyFormat.format(row.value)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function RevisionDeviationDetails({ revisionStatus, revisionMetadata, unitPriceValue }) {
  if (!revisionStatus && !revisionMetadata) return null;

  const baselineUnitPrice = toNumberOrNull(revisionMetadata?.baseline_unit_price);
  const deviationRatio = toNumberOrNull(revisionMetadata?.deviation_ratio);
  const threshold = toNumberOrNull(revisionMetadata?.threshold);
  const sampleSize = toNumberOrNull(revisionMetadata?.sample_size);
  const sampleIds = Array.isArray(revisionMetadata?.sampled_record_ids)
    ? revisionMetadata.sampled_record_ids.filter(Boolean)
    : [];
  const currentUnitPrice =
    unitPriceValue != null
      ? unitPriceValue
      : baselineUnitPrice != null && deviationRatio != null
        ? baselineUnitPrice * deviationRatio
        : null;
  const thresholdUnitPrice =
    baselineUnitPrice != null && threshold != null ? baselineUnitPrice * threshold : null;
  const deviationPercent = deviationRatio != null ? Math.abs((deviationRatio - 1) * 100) : null;
  const thresholdPercent = threshold != null ? Math.abs((threshold - 1) * 100) : null;
  const isAboveBaseline = deviationRatio != null ? deviationRatio >= 1 : null;
  const thresholdBreachPercent =
    deviationRatio != null && threshold != null ? Math.abs(deviationRatio - threshold) * 100 : null;

  return (
    <Accordion disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.5,
          minHeight: 36,
          "& .MuiAccordionSummary-content": { my: 0.5 },
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          Revision deviation details
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0.5 }}>
        <Stack spacing={1.25}>
          <Typography variant="body2">
            <strong>Status:</strong> {formatRevisionStatus(revisionStatus)}
          </Typography>
          {revisionMetadata ? (
            <Stack spacing={1}>
              <Typography variant="body2">
                {deviationPercent != null && isAboveBaseline != null ? (
                  <>
                    This row is <strong>{deviationPercent.toFixed(1)}%</strong>{" "}
                    {isAboveBaseline ? "above" : "below"} the baseline median.
                  </>
                ) : (
                  "This row was flagged for revision due to price deviation from the baseline."
                )}
              </Typography>

              {deviationPercent != null && thresholdPercent != null ? (
                <Typography variant="body2" color="text.secondary">
                  Alert threshold for this {formatWindow(revisionMetadata?.window)} window is{" "}
                  {thresholdPercent.toFixed(1)}%. This row exceeds the threshold by{" "}
                  <strong>{Math.max(0, thresholdBreachPercent || 0).toFixed(1)}%</strong>.
                </Typography>
              ) : null}

              {baselineUnitPrice != null && currentUnitPrice != null && thresholdUnitPrice != null ? (
                <PriceDeviationBars
                  baselineUnitPrice={baselineUnitPrice}
                  currentUnitPrice={currentUnitPrice}
                  thresholdUnitPrice={thresholdUnitPrice}
                />
              ) : null}

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                {sampleSize != null ? (
                  <Chip size="small" variant="outlined" label={`Sample size: ${sampleSize}`} />
                ) : null}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Window: ${formatWindow(revisionMetadata?.window)}`}
                />
                {threshold != null ? (
                  <Chip size="small" variant="outlined" label={`Ratio threshold: ${threshold.toFixed(4)}x`} />
                ) : null}
                {deviationRatio != null ? (
                  <Chip size="small" color="warning" label={`Observed ratio: ${deviationRatio.toFixed(4)}x`} />
                ) : null}
              </Stack>

              {sampleIds.length > 0 ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Baseline sample record IDs used in this comparison:
                  </Typography>
                  <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                    {sampleIds.map((sampleId) => (
                      <Chip key={String(sampleId)} size="small" label={String(sampleId)} />
                    ))}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No revision metadata is available for this row.
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default RevisionDeviationDetails;
